<template>
  <span>
    Tu ambiente de trabajo está en plan 
    <span style="font-weight: 500;">
      {{ planTenant != PlanSpecial ? PlanTypeText[planTenant as PlanType] : PlanTypeText[PlanType.AllInOne] }}. 
    </span>
    Conoce más información en 
    <a type="link" class="link" :href="linkPlans" target="_blank">
      <span>
        Planes y precios
      </span>
      <IconExternalLink active />
    </a>
  </span>
  <CustomScrollbar class="scrollbar" height="calc(100vh - 180px)" auto style="width: 100%">
    <div class="select_content">
      <div class="select_grid">
        <template
          v-for="(plan, key) in plans"
          :key="key"
        >
          <a-card
            @click="selectPlan(plan)"
            class="plan"
            :class="{
              'plan_selected': planSelected?.id == plan.id,
              'plan-popular': plan.id == PlanType.AllInOne
            }"
            :title="plan.id == PlanType.AllInOne ? 'MÁS POPULAR' : ''"
          > 
            <div class="plan_header">
              <img :src="urlImage(planTypeIcon(plan.id))" alt="plan" style="height: 56px" />
              <a-tag
                color="warning"
                v-if="
                  plan.id == planTenant ||
                  (planTenant == PlanSpecial && plan.id == PlanType.AllInOne)
                ">
                Plan actual
              </a-tag>
            </div>
            <h3>{{ plan.name }}</h3>
            <div class="price">
              <span class="price_slot">
                ${{ plan.id == planTenant ? pricePerSlot : plan.configuration.price_per_slot_base.value }} 
              </span>
              <span class="price_description">
                por slot/mes
              </span>
              <p class="price_info">
                <InfoCircleOutlined />
                IGV no incluido
              </p>
            </div>
            <div v-if="planTenant != PlanSpecial">
              <a-button
                v-if="plan.id == PlanType.AllInOne"
                class="button_preview button_popular"
                @click.stop="showModalRequest(plan.id)"
              >
                Contactar un asesor
              </a-button>
              <a-button
                v-else-if="plan.id == planTenant"
                class="button_preview"
                @click.stop="showModalRequest(plan.id)"
              >
                Personalizar plan
                <EditOutlined />
              </a-button>
              <a-button
                v-else-if="plan.id > planTenant"
                class="button_preview button_upgrade"
                @click.stop="showModalRequest(plan.id)"
              >
                Upgrade
                <RocketOutlined />
              </a-button>
              <a-button
                v-else-if="plan.id < planTenant"
                class="button_preview"
                @click.stop="showModalRequest(plan.id)"
              >
                Downgrade
                <ArrowDownOutlined />
              </a-button>
              <a-button type="text" class="button_preview" style="margin-top: 0" @click.stop="showModalPreview(plan)">
                Previsualizar plan
              </a-button>
            </div>
            <a-divider class="divider" />
            <div class="details">
              <p 
                v-for="(detail, key) in plan.configuration.details"
                class="details_item"
                :class="{ 'details_item--ai': detail.ai }"
                :key="key"
              >
                <component 
                  :style="detail.icon == 'PhoneOutlined' ? 'transform: scaleX(-1)' : ''"
                  v-if="IconComponent(detail.icon) || detail.ai"
                  :is="IconComponent(detail.icon)"
                />
                <div>
                  <span>{{ (plan.id == planTenant && key == 0) ? `${slotsPerMonth} slots mínimo` : detail.label }}</span>
                  <!-- <BeexAIIcon v-if="detail.ai" /> -->
                </div>
              </p>
            </div>
            <a-divider class="divider" />
            <p class="channels_title">Canales disponibles:</p>
            <div class="channels">
              <div 
                v-for="(icon, type) in filteredChannels(plan.configuration.channels)" 
                :key="type"
                class="channels_item"
              >
                <SocialIcon
                  :type="icon" 
                  width="20" 
                />
                <span class="channels_item--text">
                  {{ ChannelTypeText[type] }}
                </span>
              </div>
            </div>
          </a-card>
        </template>
        
      </div>
    </div>
  </CustomScrollbar>
  
  <preview-plan-modal
    v-model:open="openPreview"
    :plan="planPreview"
    isCustomerConfig
  />
  <request-change-plan-modal
    v-model:open="visibleModalRequest"
    :idPlan="planTenant"
    :idPlanChange="planRequest"
  />
</template>

<script setup lang="ts">
import { onMounted, ref, inject, computed } from "vue";
import { 
  InfoCircleOutlined, 
  EyeOutlined,
  UserOutlined,
  RocketOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ArrowDownOutlined,
  EditOutlined,
} from "@ant-design/icons-vue";
// import { ChannelType, ChannelTypeAttribute, PlanPayType, IPlan } from "@/app/tenant/types";
// import { ChannelTypeIcon, ChannelTypeText, PlanTypeIcon } from "@/app/tenant/utils/texts";
import { plansService } from "@/app/settings/services/plans/plan.service";
// import { PlanAction } from "@/app/tenant/types";
import SocialIcon from "@/app/common/components/icons/SocialIcon.vue";
import CheckAIIcon from "@/app/common/components/icons/CheckAIIcon.vue";
import BeexAIIcon from "@/app/common/components/icons/BeexAIIcon.vue";
import { ChannelType, ChannelTypeAttribute, IPlan, PlanAction, PlanPayType, PlanType, PlanTypeText } from "@/app/common/types";
import { ChannelTypeIcon, ChannelTypeText, PlanTypeIcon } from "@/app/common/utils/texts";
import httpManager from "@/app/shared/utils/httpManager";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { UserData } from "@/@types/global";
import { useStore } from "vuex";
import RequestChangePlanModal from "../modals/RequestChangePlanModal.vue";
import PreviewPlanModal from "../modals/PreviewPlanModal.vue";
import { planConfigServices } from "@/app/common/services/PlanConfigServices";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import UnionIcon from "@/app/common/components/icons/UnionIcon.vue";

const store = useStore();

const plans = ref<IPlan[]>([]);
const openPreview = ref(false);
const planPreview = ref();
const planSelected = inject<any>('planSelected');
const planSelectedLast = inject<any>('planSelectedLast');
const user = computed<UserData | undefined>(() => store.getters["getDataUser"]);

// const plan = computed(() => user.value?.plan_id);
const planTenant = computed(() => user.value?.change_custom_plan?.plan);
const planConfigTenant = computed(() => user.value?.change_custom_plan?.custom_plan_config);
const pricePerSlot = computed(() => user.value?.change_custom_plan?.slot_price);
const slotsPerMonth = computed(() => user.value?.change_custom_plan?.slot_max);
const planRequest = ref();
const visibleModalRequest = ref(false);
const PlanSpecial = 5;
const linkPlans = `${import.meta.env.VITE_CONTACT_CENTER_BEEX}precios/`;

defineProps({
  action: {
    type: String,
    default: null,
    required: false,
  }
});

const emit = defineEmits<{ "nextStep": [] }>();

const icons = {
  InfoCircleOutlined,
  EyeOutlined,
  UserOutlined,
  RocketOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CheckAIIcon,
  BeexAIIcon,
  UnionIcon,
};

const planTypeIcon = (id: number | undefined) => {
  return PlanTypeIcon[id as keyof typeof PlanTypeIcon];
};

const IconComponent = (icon: keyof typeof icons) => {
  return icons[icon] || null
};

const filteredChannels = (channels: any) => {
  return Object.entries(ChannelTypeIcon).reduce((acc, [type, icon]) => {
    const attribute = ChannelTypeAttribute[type as ChannelType];
    if (channels[attribute]) {
      acc[type as ChannelType] = icon;
    }
    return acc;
  }, {} as Record<ChannelType, string>);
};

const urlImage = (image: string) => {
  return new URL(`../../../common/assets/${image}`, import.meta.url).href;
};

const fetchPlanData = async (id: number) => {
  // httpManager.updateBaseURL(`https://backend.${import.meta.env.VITE_HOSTNAME}`);
  const response = await planConfigServices.getDataPlanClientById(id);
  // const workspace = localStorage.getItem('workspace');
  // httpManager.updateBaseURL(`https://${workspace}.backend.${import.meta.env.VITE_HOSTNAME}`);
  return response;
};

const showModalPreview = async (plan: any) => {
  console.log(plan.id, 'plan id');
  console.log(planTenant.value, 'planTenant.value');
  if (plan.id == planTenant.value ||
    (planTenant.value == PlanSpecial && plan.id == PlanType.AllInOne)
  ) {
    const response: any = await fetchPlanData(planConfigTenant.value);
    console.log(response, 'response fetch plan data');
    planPreview.value = { ...response, planId: response.plan };
  } else {
    planPreview.value = { ...plan, plan: plan.id };
  }
  openPreview.value = true;
}

const showModalRequest = (planId: number) => {
  visibleModalRequest.value = true;
  planRequest.value = planId;
}

const selectPlan = (plan: IPlan) => {
  planSelected.value = {
    ...plan,
    plan: plan.id,
    type_plan_license: PlanPayType.Prepaid,
    type_plan_consume: PlanPayType.Prepaid,
  };
}

onMounted(async () => {
  // httpManager.updateBaseURL(`${import.meta.env.VITE_HOST_PADMIN}`);
  httpManager.updateBaseURL(`https://backend.${import.meta.env.VITE_HOSTNAME}`);
  plans.value = await plansService.fetchPlans();
  console.log(plans.value, 'PLANS ONMOUNTED');
  if (plans.value) {
    plans.value = plans.value.filter((el: any) => el?.id < PlanSpecial);
  }
  const workspace = localStorage.getItem('workspace');
  httpManager.updateBaseURL(`https://${workspace}.backend.${import.meta.env.VITE_HOSTNAME}`);
});
</script>

<style lang="sass" scoped>

.select
  &_content
    text-align: center
    margin-top: 20px
    h2
      margin: 30px
  &_grid
    text-align: left
    display: grid
    // grid-template-columns: repeat(auto-fit, minmax(275.5px, 1fr))
    grid-template-columns: repeat(auto-fit, minmax(205.5px, 255px))
    gap: 15px
    justify-content: center
    // .plan:hover
    //   border: 1px solid #1677FF
    .plan
      width: 100%
      border: 1px solid #00000026
      // cursor: pointer
      margin-top: 30px
      &_selected
        border: 1px solid #1677FF
      &_header
        display: flex
        align-items: flex-start
        justify-content: space-between
        .radio-select
          margin-right: 0
      .price
        &_slot
          font-size: 22px
          font-weight: 600
        &_description
          font-size: 14px
          color: $gray-8
        &_info
          color: $gray-8
          font-style: italic
    .button
      &_preview
        width: 100%
        margin: 20px 0 10px 0
      &_popular
        background: linear-gradient(180deg, #762ECF 0%, #D62FA0 100%)
        border: 1px solid
        border-image-source: linear-gradient(180deg, #762ECF 0%, #D62FA0 100%)
        border: none
        color: #fff
      &_upgrade
        background-color: #1677FF
        border-color: #1677FF
        color: #fff
    .details
      margin: 40px 0 0px 0
      &_item
        display: flex
        align-items: flex-start
        .anticon
          margin-top: 4px
          margin-right: 8px
        div
          width: 100%
          display: flex
          align-items: center
          span
            margin-right: 8px
        &--ai
          background: linear-gradient(180deg, #762ECF 0%, #D62FA0 100%)
          -webkit-background-clip: text
          -webkit-text-fill-color: transparent
    .channels_title
      margin-top: 30px
      margin-bottom: 10px
      font-size: 14px
    .channels
      column-count: 2
      margin-bottom: 10px
      &_item
        break-inside: avoid
        display: flex
        align-items: center
        gap: 10px
        margin-bottom: 10px
        .anticon
          height: 20px
        &--text
          margin-left: 5px
.divider
  position: absolute
  left: 0
  margin: 10px 0

:deep(.plan .ant-card-body)
  padding: 15px 20px !important

.footer
  margin: 20px 0
  display: flex
  justify-content: center

.plan-popular
  height: 100%
  margin-top: 0 !important
:deep(.plan-popular .ant-card-head)
  background: linear-gradient(180deg, #762ECF 0%, #D62FA0 100%)
  color: #fff
  text-align: center
  min-height: 32px
  font-size: 14px
  // .plan
  //   border: 1px solid transparent
  //   border-image-source: linear-gradient(180deg, #762ECF 0%, #D62FA0 100%)
  //   border-image-slice: 1
  .plan-popular::before
    content: ""
    position: absolute
    inset: 0
    border-radius: 8px
    padding: 1px
    background: linear-gradient(180deg, #762ECF 0%, #D62FA0 100%)
    mask: linear-gradient(white, white) content-box, linear-gradient(white, white)
    mask-composite: exclude
    -webkit-mask: linear-gradient(white, white) content-box, linear-gradient(white, white)
    -webkit-mask-composite: xor

.link
  display: inline-flex
  align-items: center
  gap: 2px
  font-weight: 400
  span
    text-decoration: underline

:global(.scrollbar .ps__rail-x)
  display: none !important
</style>